import { HanamiElement, createShadow, define, prefersReducedMotion } from '../core/base';
import { PETAL_PATH } from '../core/shapes';
import { onThemeChange } from '../core/theme';

const css = `:host{position:absolute;inset:0;display:block;overflow:hidden;pointer-events:none}canvas{display:block;width:100%;height:100%}`;

interface Petal {
  x: number;
  y: number;
  size: number;
  vy: number;
  sway: number;
  swaySpeed: number;
  phase: number;
  rotation: number;
  spin: number;
  flip: number;
  flipSpeed: number;
  color: string;
  alpha: number;
}

/**
 * Pétales de sakura qui tombent en douceur (canvas, décoratif).
 * Se met en pause hors écran, onglet masqué ou mouvement réduit (pétales fixes).
 *
 * Attributs : count (défaut 24), speed (1), wind (1), size (1), fixed, paused.
 * Couleurs : --hn-color-petal, --hn-color-petal-alt, --hn-color-petal-deep.
 */
export class HnPetals extends HanamiElement {
  static readonly tagName = 'hn-petals';
  static get observedAttributes() {
    return ['count', 'paused', 'size'];
  }

  private canvas!: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D | null = null;
  private petals: Petal[] = [];
  private frame = 0;
  private last = 0;
  private width = 0;
  private height = 0;
  private visible = true;
  private colors: string[] = [];
  private path?: Path2D;
  private resizeObserver?: ResizeObserver;
  private intersection?: IntersectionObserver;
  private stopTheme?: () => void;

  connectedCallback(): void {
    if (!this.shadowRoot) {
      const root = createShadow(this, css, '<canvas part="canvas"></canvas>');
      this.canvas = root.querySelector('canvas')!;
    }
    this.setAttribute('aria-hidden', 'true');
    this.ctx = this.canvas.getContext('2d');
    if (!this.ctx) return;
    this.path = typeof Path2D !== 'undefined' ? new Path2D(PETAL_PATH) : undefined;
    this.readColors();
    this.stopTheme = onThemeChange(() => requestAnimationFrame(() => this.readColors()));

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(this);
    }
    if (typeof IntersectionObserver !== 'undefined') {
      this.intersection = new IntersectionObserver(entries => {
        this.visible = entries.some(e => e.isIntersecting);
        this.syncLoop();
      });
      this.intersection.observe(this);
    }
    document.addEventListener('visibilitychange', this.syncLoop);
    this.resize();
  }

  disconnectedCallback(): void {
    cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.resizeObserver?.disconnect();
    this.intersection?.disconnect();
    this.stopTheme?.();
    document.removeEventListener('visibilitychange', this.syncLoop);
  }

  attributeChangedCallback(name: string): void {
    if (!this.ctx) return;
    if (name === 'count' || name === 'size') this.populate();
    this.syncLoop();
  }

  private readColors(): void {
    const style = getComputedStyle(this);
    this.colors = ['--hn-color-petal', '--hn-color-petal-alt', '--hn-color-petal-deep']
      .map(v => style.getPropertyValue(v).trim())
      .filter(Boolean);
    if (!this.colors.length) this.colors = ['#F8AFC9', '#F7D2DE', '#F684AF'];
    this.petals.forEach((p, i) => (p.color = this.colors[i % this.colors.length]));
    if (!this.frame) this.draw();
  }

  private resize(): void {
    const rect = this.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.width = rect.width;
    this.height = rect.height;
    this.canvas.width = Math.max(1, Math.round(rect.width * dpr));
    this.canvas.height = Math.max(1, Math.round(rect.height * dpr));
    this.ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);
    if (!this.petals.length) this.populate();
    this.draw();
    this.syncLoop();
  }

  private spawn(initial: boolean): Petal {
    const scale = this.getNumber('size', 1);
    const size = (9 + Math.random() * 11) * scale;
    return {
      x: Math.random() * (this.width + 120) - 60,
      y: initial ? Math.random() * this.height : -size - Math.random() * 60,
      size,
      vy: 18 + Math.random() * 26,
      sway: 14 + Math.random() * 26,
      swaySpeed: 0.4 + Math.random() * 0.8,
      phase: Math.random() * Math.PI * 2,
      rotation: Math.random() * Math.PI * 2,
      spin: (Math.random() - 0.5) * 1.6,
      flip: Math.random() * Math.PI * 2,
      flipSpeed: 1 + Math.random() * 2.2,
      color: this.colors[Math.floor(Math.random() * this.colors.length)] ?? '#F8AFC9',
      alpha: 0.55 + Math.random() * 0.4,
    };
  }

  private populate(): void {
    const count = Math.max(0, Math.min(150, Math.round(this.getNumber('count', 24))));
    this.petals = Array.from({ length: count }, () => this.spawn(true));
    if (!this.frame) this.draw();
  }

  private get animating(): boolean {
    return this.visible && !document.hidden && !this.getBool('paused') && !prefersReducedMotion() && this.isConnected;
  }

  private syncLoop = (): void => {
    if (this.animating) {
      if (!this.frame) {
        this.last = performance.now();
        this.frame = requestAnimationFrame(this.tick);
      }
    } else if (this.frame) {
      cancelAnimationFrame(this.frame);
      this.frame = 0;
    }
  };

  private tick = (now: number): void => {
    const dt = Math.min(0.05, (now - this.last) / 1000);
    this.last = now;
    const speed = this.getNumber('speed', 1);
    const wind = this.getNumber('wind', 1);
    for (let i = 0; i < this.petals.length; i++) {
      const p = this.petals[i];
      p.phase += p.swaySpeed * dt;
      p.flip += p.flipSpeed * dt;
      p.rotation += p.spin * dt;
      p.y += p.vy * speed * dt;
      p.x += (Math.sin(p.phase) * p.sway + 16 * wind) * speed * dt;
      if (p.y > this.height + p.size * 2 || p.x > this.width + 80 || p.x < -80) this.petals[i] = this.spawn(false);
    }
    this.draw();
    this.frame = requestAnimationFrame(this.tick);
  };

  private draw(): void {
    const ctx = this.ctx;
    if (!ctx || !this.path) return;
    ctx.clearRect(0, 0, this.width, this.height);
    for (const p of this.petals) {
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rotation);
      // Aplatissement sinusoïdal : le pétale semble tourner sur lui-même en 3D.
      ctx.scale((p.size / 100) * (0.35 + 0.65 * Math.abs(Math.cos(p.flip))), p.size / 100);
      ctx.translate(-50, -50);
      ctx.globalAlpha = p.alpha;
      ctx.fillStyle = p.color;
      ctx.fill(this.path);
      ctx.restore();
    }
  }
}

define(HnPetals.tagName, HnPetals);
